import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import CAppDialog from '../../../../app/components/CAppDialog';
import FAuthOktaFactorQuestion from '../../../auth/features/okta-factor-question/FAuthOktaFactorQuestion';
import FAuthOktaFactorSms from '../../../auth/features/okta-factor-sms/FAuthOktaFactorSms';
import langUserProfile from '../../langs/langUserProfile';
import { Avatar, Box, Button, ListItemText, Stack, SxProps, useTheme } from '@mui/material';
import { MailOutlined, PhoneOutlined, QuestionMarkOutlined } from '@mui/icons-material';
import useAuthOktaFactorQuestionMutation from '../../../auth/features/okta-factor-question/hooks/useAuthOktaFactorQuestionMutation';
import useAuthOktaFactorSmsMutation from '../../../auth/features/okta-factor-sms/hooks/useAuthOktaFactorSmsMutation';
import { Trans, useTranslation } from 'react-i18next';
import CAppPaper from '../../../../app/components/CAppPaper';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import { FieldBox } from '../../pages/PUserProfile';
import useAuthOktaUserFactorsCatalog from '../../../auth/hooks/okta/useAuthOktaUserFactorsCatalog';
import _ from 'lodash';

type VUserProfileSecurityFactorsProps = {
	sxStyle: SxProps;
	sxEditButton: SxProps;
	disableEdit: boolean;
};

const VUserProfileSecurityFactors = (props: VUserProfileSecurityFactorsProps) => {
	const { sxStyle, sxEditButton, disableEdit } = props;

	const { t } = useTranslation();

	const { data: securityFactorsCatalog } = useAuthOktaUserFactorsCatalog();

	const [editBlock, setEditBlock] = useState(false);

	const handleClickEditSecurityMethod = () => {
		setEditBlock(!editBlock);
	};

	return (
		<CAppPaper sx={sxStyle} variant="outlined">
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Box className="title">
					<AppCustomIcon tag="user_security" className="icon" />{' '}
					{t(langUserProfile.title_security_method.tag)}
				</Box>
				<Box>
					{editBlock ? (
						<Button
							variant="contained"
							color="secondary"
							size="small"
							sx={sxEditButton}
							disabled={disableEdit}
							startIcon={<AppCustomIcon tag="save" className="icon" />}
							onClick={() => handleClickEditSecurityMethod()}
						>
							{t(langUserProfile.btn_close.tag)}
						</Button>
					) : (
						<Button
							variant="text"
							color="secondary"
							size="small"
							sx={sxEditButton}
							disabled={disableEdit}
							startIcon={<AppCustomIcon tag="edit" className="icon" />}
							onClick={() => handleClickEditSecurityMethod()}
						>
							{t(langUserProfile.btn_edit.tag)}
						</Button>
					)}
				</Box>
			</Stack>
			{!disableEdit ? (
				<FieldBox
					bEdit={editBlock}
					value={
						<>
							<Trans
								t={t}
								i18nKey={langUserProfile.text_you_have_configured.tag}
								values={{
									nbSecurityMethod: securityFactorsCatalog
										? securityFactorsCatalog.filter((x) => x.status === 'ACTIVE').length
										: null,
									totalSecurityMethod: securityFactorsCatalog ? securityFactorsCatalog.length : null,
								}}
							/>
						</>
					}
					component={
						<>
							<SecurityFactors factors={securityFactorsCatalog ?? null} />
							{/* <Stack
													direction="row"
													spacing={2}
													alignItems="center"
													justifyContent="left"
												>
													<Box>Email</Box>
													<CAppSwitch
														checked={profileData.securityMethod === 'sms' ? true : false}
														onChange={() =>
															setProfileData((prevData) => ({
																...prevData,
																securityMethod:
																	profileData.securityMethod === 'sms'
																		? 'email'
																		: 'sms',
															}))
														}
													/>
													<Box>SMS</Box>
												</Stack> */}
						</>
					}
				/>
			) : (
				''
			)}
		</CAppPaper>
	);
};

interface IAuthOktaSecurityPhone {
	id: string;
	profile: { phoneNumber: string };
	status: string;
}

interface IAuthOktaSecurityEmail {
	id: string;
	profile: { email: string };
	status: string;
}

interface IAuthOktaSecurityFactor {
	status?: string;
	factorType: string;
	provider: string;
	_embedded?: {
		phones?: IAuthOktaSecurityPhone[];
		emails?: IAuthOktaSecurityEmail[];
	};
}

type SecurityFactorsProps = {
	factors: IAuthOktaSecurityFactor[] | null;
};

const SecurityFactors = (props: SecurityFactorsProps) => {
	const { factors } = props;

	// Display order: SMS, then security question, then email
	return (
		<>
			{factors ? (
				<Box>
					{_.orderBy(factors, ['status', 'factorType'], ['desc', 'desc']).map((factor, index) => {
						return <SecurityFactorItem factor={factor} key={index} />;
					})}
				</Box>
			) : (
				'Loading factors...'
			)}
		</>
	);
};

interface IAuthSecurityFactor {
	type: 'email' | 'sms' | 'question';
	icon: ReactNode;
	name: string;
	shortName: string;
	isSetup?: boolean | null;
	canRemove: boolean;
}

type SecurityFactorItemProps = {
	factor: IAuthOktaSecurityFactor;
};

const SecurityFactorItem = (props: SecurityFactorItemProps) => {
	const { factor } = props;
	const theme = useTheme();
	const { t } = useTranslation();

	const {
		mutateDeleteFactor: { mutate: deleteFactorQuestion },
	} = useAuthOktaFactorQuestionMutation();

	const {
		mutateDeleteFactor: { mutate: deleteFactorSms },
	} = useAuthOktaFactorSmsMutation();

	const FACTORS: IAuthSecurityFactor[] = useMemo(
		() => [
			{
				type: 'email',
				icon: <MailOutlined />,
				name: t(langUserProfile.factor_list_email_title.tag),
				shortName: t(langUserProfile.factor_list_email_title_short.tag),
				isSetup: null,
				canRemove: false,
			},
			{
				type: 'sms',
				icon: <PhoneOutlined />,
				name: t(langUserProfile.factor_list_sms_title.tag),
				shortName: t(langUserProfile.factor_list_sms_title_short.tag),
				isSetup: null,
				canRemove: true,
			},
			{
				type: 'question',
				icon: <QuestionMarkOutlined />,
				name: t(langUserProfile.factor_list_question_title.tag),
				shortName: t(langUserProfile.factor_list_question_title_short.tag),
				isSetup: null,
				canRemove: true,
			},
		],
		[t],
	);

	const [currentFactor, setCurrentFactor] = useState<IAuthSecurityFactor | null>(null);
	const [embeddedPhones, setEmbbededPhones] = useState<IAuthOktaSecurityPhone[] | null>(null);
	const [embeddedEmails, setEmbbededEmails] = useState<IAuthOktaSecurityEmail[] | null>(null);
	useEffect(() => {
		if (factor && FACTORS) {
			const currentFactorTmp = FACTORS.find((x) => x.type === factor.factorType);
			// currentFactorTmp.isSetup = factor.status === 'ACTIVE' ? true : false;

			setCurrentFactor(currentFactorTmp ?? null);

			if (factor._embedded && factor._embedded.phones) {
				setEmbbededPhones(factor._embedded.phones);
			} else {
				setEmbbededPhones(null);
			}
			if (factor._embedded && factor._embedded.emails) {
				setEmbbededEmails(factor._embedded.emails);
			} else {
				setEmbbededEmails(null);
			}
		} else {
			setCurrentFactor(null);
			setEmbbededPhones(null);
			setEmbbededEmails(null);
		}
	}, [factor, FACTORS]);

	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleSetupFactor = () => {
		setIsModalOpen(true);
	};

	const handleRemoveFactor = () => {
		if (currentFactor?.canRemove) {
			if (currentFactor.type === 'question') {
				deleteFactorQuestion();
			} else if (currentFactor.type === 'sms') {
				deleteFactorSms();
			}
		}
	};

	return (
		<>
			{currentFactor ? (
				<>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="center"
						spacing={3}
						sx={{
							backgroundColor: theme.palette.background.default,
							padding: '12px',
							marginBottom: '10px',
							borderRadius: '6px',
						}}
					>
						<Avatar sx={{ backgroundColor: theme.palette.primary.contrastText }}>
							{currentFactor.icon}
						</Avatar>
						<ListItemText
							primary={currentFactor.name}
							secondary={
								<>
									{embeddedEmails
										? embeddedEmails.map((email) => email.profile.email).join(',')
										: null}
									{embeddedPhones
										? embeddedPhones.map((phone) => phone.profile.phoneNumber).join(', ')
										: null}
								</>
							}
						/>
						{factor.status !== 'ACTIVE' ? (
							<Button
								color="secondary"
								variant="contained"
								size="small"
								onClick={() => handleSetupFactor()}
							>
								{t(langUserProfile.factor_btn_setup.tag)}
							</Button>
						) : (
							<Button
								disabled={!currentFactor.canRemove}
								color="primary"
								variant="outlined"
								size="small"
								onClick={() => handleRemoveFactor()}
							>
								{t(langUserProfile.factor_btn_remove.tag)}
							</Button>
						)}
					</Stack>
					<CAppDialog
						title={`${currentFactor.shortName}`}
						open={isModalOpen}
						maxWidth="sm"
						onClose={() => setIsModalOpen(false)}
					>
						{currentFactor.type === 'question' ? (
							<FAuthOktaFactorQuestion onSave={() => setIsModalOpen(false)} />
						) : currentFactor.type === 'sms' ? (
							<FAuthOktaFactorSms onSave={() => setIsModalOpen(false)} />
						) : currentFactor.type === 'email' ? (
							<>FAuthOktaFactorEmail</>
						) : (
							''
						)}
					</CAppDialog>
				</>
			) : (
				''
			)}
		</>
	);
};

export default VUserProfileSecurityFactors;
